import { green, grey, orange } from "@mui/material/colors";
import {
    Box,
    Button,
    Fade,
    Grid,
    Tooltip,
    Typography,
    useMediaQuery
} from "@mui/material";
import { useEffect, useState } from "react";
import { useCommonItems } from "../../../../../../contexts/CommonItensProvider";
import { SetProductThirdStep, TutorialLockerThirdStepPanel } from "./components/TutorialLockerThirdStepPanel";
import { listaProdutos } from "../../../products/configs/functions";
import {
    buildLockerCells,
    getDoorColors,
    getEffectiveDoorStatus,
    GROUP_BORDER,
    LOCKER_LEGEND,
    recalcLogical
} from "./config/functions";
import { useAppContext } from "../../../../../../contexts/AppContext";

export function ThirdStepLockerForm({ values, portas, setPortas, qtdColunas }) {
    const { exibirAlerta } = useCommonItems();
    const { dadosUsuario } = useAppContext();

    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));

    const cols = Math.max(1, parseInt(qtdColunas) || 1);

    const sz = isSmall ? 36 : 48;
    const gap = 8;

    const [portaSelecionada, setPortaSelecionada] = useState(null);
    const [produtos, setProdutos] = useState([]);
    const [loadingProdutos, setLoadingProdutos] = useState(false);

    useEffect(() => {
        const load = async () => {
            if (!values?.id_empresa) {
                setProdutos([]);

                return;
            }

            setLoadingProdutos(true);

            try {
                const res = await listaProdutos({
                    id_empresa: values.id_empresa,
                    isFilter: true
                });

                setProdutos(res.data.data);
            } catch {
                exibirAlerta('Erro', 'Erro ao buscar produtos', 'error');
            } finally {
                setLoadingProdutos(false);
            }
        };

        load();
    }, [values?.id_empresa]);

    const applyToGroup = (gid, patch) =>
        portas.map(porta =>
            String(porta.groupId) === String(gid)
                ? { ...porta, ...patch, is_dirty: true }
                : porta
        );

    const applyToSingle = (idx, patch) =>
        portas.map((porta, index) =>
            index === idx
                ? { ...porta, ...patch, is_dirty: true }
                : porta
        );

    const handleClickPorta = cell => {
        const configDoor = cell.configDoor ?? cell.porta;

        if (getEffectiveDoorStatus(configDoor) === 'desativado') {
            return;
        }

        setPortaSelecionada(cell.physIdx);
    };

    const handleConfirm = ({ id_produto, id_produto_pai, qtd_produto }) => {
        const portaAtual = portas[portaSelecionada];

        const gid = portaAtual?.groupId ?? null;

        const patch = {
            id_produto,
            id_produto_pai,
            qtd_produto,
            mustSelectChild: false
        };

        if (gid !== null && gid !== undefined) {
            setPortas(applyToGroup(gid, { ...patch, doorStatus: 'ocupado' }));
        } else {
            setPortas(applyToSingle(portaSelecionada, { ...patch, status: 'ocupado' }));
        }

        setPortaSelecionada(null);
    };

    const handleRemove = () => {
        const portaAtual = portas[portaSelecionada];

        const gid = portaAtual?.groupId ?? null;

        const patch = {
            id_produto: null,
            id_produto_pai: null,
            qtd_produto: 0,
            mustSelectChild: false
        };

        if (gid !== null && gid !== undefined) {
            setPortas(applyToGroup(gid, { ...patch, doorStatus: 'livre' }));
        } else {
            setPortas(applyToSingle(portaSelecionada, { ...patch, status: 'livre' }));
        }

        setPortaSelecionada(null);
    };

    const handleSetStatus = novoStatus => {
        const portaAtual = portas[portaSelecionada];

        const gid = portaAtual?.groupId ?? null;

        const clearProduct =
            novoStatus !== 'livre' && novoStatus !== 'ocupado'
                ? {
                    id_produto: null,
                    id_produto_pai: null,
                    qtd_produto: 0,
                    mustSelectChild: false
                }
                : {};

        let updated;

        if (gid !== null && gid !== undefined) {
            updated = applyToGroup(gid, { doorStatus: novoStatus, ...clearProduct });
        } else {
            updated = applyToSingle(portaSelecionada, { status: novoStatus, ...clearProduct });
        }

        setPortas(recalcLogical(updated));

        setPortaSelecionada(null);
    };

    const rows = portas.length > 0 ? Math.ceil(portas.length / cols) : 1;

    const cells = buildLockerCells(portas, cols);

    const portaSel = portaSelecionada !== null ? portas[portaSelecionada] : null;

    const statusSel = portaSel ? getEffectiveDoorStatus(portaSel) : null;

    const STATUS_LABEL = {
        livre: { label: 'Livre', color: green[700], bg: green[50] },
        ocupado: { label: 'Ocupado', color: green[900], bg: green[100] },
        desativado: { label: 'Inativada', color: grey[700], bg: grey[100] },
        aguardando: { label: 'Aguardando', color: grey[700], bg: grey[100] },
    };

    return (
        <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="flex-start"
            spacing={3}
            sx={{ mt: 1 }}
        >
            <Grid item xs={12}>
                <Typography variant="h5" textAlign="center" gutterBottom>
                    {dadosUsuario.isSempher
                        ? 'Designação de Produtos'
                        : 'Visualização de Produtos'
                    }
                </Typography>
            </Grid>

            <Grid item xs={12} md={7}>
                <Box
                    sx={{
                        border: `1px solid ${grey[300]}`,
                        borderRadius: 2,
                        p: 2
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${cols}, ${sz}px)`,
                            gridTemplateRows: `repeat(${rows}, ${sz}px)`,
                            gap: `${gap}px`,
                            justifyContent: 'center',
                        }}
                    >
                        {cells.map(cell => {
                            const { configDoor, physIdx, gridRow, gridCol, rowSpan, colSpan, grouped } = cell;

                            const effStatus = getEffectiveDoorStatus(configDoor);

                            const isSelected = physIdx === portaSelecionada;

                            const { bg, hoverBg, border, color } = getDoorColors(configDoor);

                            // Borda: selecionada -> laranja; agrupada -> tracejada; senão -> status.
                            let borderStyle;
                            if (isSelected) {
                                borderStyle = `2.5px solid ${orange[600]}`;
                            } else if (grouped) {
                                borderStyle = GROUP_BORDER;
                            } else {
                                borderStyle = `1.5px solid ${border}`;
                            }

                            const btnSx = {
                                gridColumn: `${gridCol} / span ${colSpan}`,
                                gridRow: `${gridRow} / span ${rowSpan}`,
                                width: `${colSpan * sz + (colSpan - 1) * gap}px`,
                                height: `${rowSpan * sz + (rowSpan - 1) * gap}px`,
                                minWidth: 0,
                                p: 0,
                                fontSize: 12,
                                fontWeight: 500,
                                borderRadius: 1.5,
                                border: borderStyle,
                                backgroundColor: isSelected ? orange[100] : bg,
                                color: isSelected ? orange[900] : color,
                                transform: isSelected ? 'scale(1.04)' : 'none',
                                transition: 'all .15s',
                                cursor: effStatus === 'desativado' ? 'not-allowed' : 'pointer',
                                '&:hover': {
                                    backgroundColor: isSelected ? orange[200] : hoverBg,
                                    color: 'white'
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: bg,
                                    color,
                                    opacity: 1,
                                    border: grouped ? GROUP_BORDER : `1.5px solid ${border}`,
                                },
                            };

                            const tooltipTitle = effStatus === 'desativado'
                                ? 'Porta inativada — reative-a para designar produto.'
                                : grouped
                                    ? (configDoor.id_produto ? 'Grupo com produto — clique para editar' : 'Clique para designar um produto ao grupo')
                                    : configDoor.id_produto
                                        ? 'Clique para editar o produto'
                                        : 'Clique para designar um produto';

                            return (
                                <Tooltip
                                    key={`${physIdx}-${configDoor.groupId ?? 'single'}`}
                                    title={tooltipTitle}
                                    placement="top"
                                >
                                    <span
                                        style={{
                                            gridColumn: `${gridCol} / span ${colSpan}`,
                                            gridRow: `${gridRow} / span ${rowSpan}`
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            disabled={effStatus === 'desativado'}
                                            onClick={() => handleClickPorta(cell)}
                                            sx={btnSx}
                                        >
                                            {(!configDoor.numero_logico || effStatus === 'desativado')
                                                ? '—'
                                                : configDoor.numero_logico
                                            }
                                        </Button>
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                        mt: 1.5,
                        pl: .5
                    }}
                >
                    {LOCKER_LEGEND.map(item => (
                        <Box
                            key={item.label}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: .75
                            }}
                        >
                            <Box
                                sx={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: '3px',
                                    backgroundColor: item.bg,
                                    border: item.dashed
                                        ? `2px dashed ${item.border}`
                                        : `1.5px solid ${item.border}`
                                }}
                            />

                            <Typography variant="caption" color="text.secondary">
                                {item.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {portaSel !== null && dadosUsuario.isSempher && (() => {
                    const badge = STATUS_LABEL[statusSel] ?? STATUS_LABEL.livre;

                    const isAguardando = statusSel === 'aguardando';
                    const isDesativado = statusSel === 'desativado';

                    return (
                        <Box
                            sx={{
                                mt: 2,
                                p: 1.5,
                                border: `1px solid ${grey[200]}`,
                                borderRadius: 2,
                                backgroundColor: grey[50],
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color="text.secondary"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: '.05em'
                                    }}
                                >
                                    Porta {portaSel.numero_logico ?? portaSelecionada + 1}
                                    {portaSel.status === 'grouped' && ' (agrupada)'}
                                </Typography>

                                <Box
                                    sx={{
                                        px: 1,
                                        py: .25,
                                        borderRadius: 1,
                                        backgroundColor: badge.bg,
                                        border: `1px solid ${badge.color}22`,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ color: badge.color }}
                                    >
                                        {badge.label}
                                    </Typography>
                                </Box>
                            </Box>

                            {(isAguardando || isDesativado) && (
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    sx={{
                                        borderColor: green[600],
                                        color: green[700],
                                        '&:hover': {
                                            borderColor: green[800],
                                            backgroundColor: green[50]
                                        }
                                    }}
                                    onClick={() => handleSetStatus('livre')}
                                >
                                    ↩ Reativar porta
                                </Button>
                            )}

                            {!isDesativado && (
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleSetStatus('desativado')}
                                >
                                    ⛔ Inativar porta
                                </Button>
                            )}
                        </Box>
                    );
                })()}
            </Grid>

            <Grid item xs={12} md={4}>
                <Fade in={portaSelecionada === null} unmountOnExit>
                    <Box>
                        <TutorialLockerThirdStepPanel />
                    </Box>
                </Fade>

                <Fade in={portaSelecionada !== null} unmountOnExit>
                    <Box>
                        {portaSel !== null && (
                            <SetProductThirdStep
                                porta={portaSel}
                                produtos={produtos}
                                loadingProdutos={loadingProdutos}
                                onConfirm={handleConfirm}
                                onRemove={handleRemove}
                                onClose={() => setPortaSelecionada(null)}
                                readOnly={!dadosUsuario.isSempher}
                            />
                        )}
                    </Box>
                </Fade>
            </Grid>
        </Grid>
    );
}